
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import moment from 'moment';
    import { SweetAlertResult } from 'sweetalert2';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import Impfung from '@/types/Impfung';
    import Patient from '@/types/Patient';
    import { ValidationRules } from '@/store/types';
    import { confirmSwal } from '@/helper/swals';
    import { VForm, createAutocompleteFilter } from '@/types/Components';
    import { LoadPersonenPayload } from '@/store/personen';
    import Leistung from '@/types/Leistung';
    import { Verrechnungsart } from '@/types/Verrechnungsart';
    import {SystemEinstellung, SystemEinstellungKey} from '@/types/Einstellung';
    import {Patgruppe} from '@/types/Patgruppe';

    @Component({
        components: {
            EingetragenAmVom,
        },
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate']),
            ...mapState('session', ['arzt', 'abrechnungLeistung']),
            ...mapState('patient', ['patientData', 'loaders']),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapState('leistungen', ['leistungen']),
            ...mapState('leistungen', { leistungenLoaders: 'loaders' }),
            ...mapGetters('personen', ['getPersonen']),
            ...mapState('personen', { personenLoaders: 'loaders' }),
            ...mapGetters('authentication', ['isAllowed', 'getSystemEinstellung']),
        },
        methods: {
            ...mapMutations('session', ['setSelectedArzt', 'setSelectedAbrechnungLeistung']),
            ...mapActions('patient', ['createLeistungseintrag', 'deleteLeistungseintrag', 'updateLeistungAbrechnung']),
            ...mapActions('leistungen', ['loadLeistungen']),
            ...mapActions('personen', ['loadPersonen']),
            ...mapMutations('patient', ['setDialogLeistung']),
        },
    })
    export default class LeistungseintragDetail extends Vue {
        @Prop() visible!: boolean;
        @Prop() value!: Impfung;

        public $refs!: {
            form: VForm;
        };

        public rules!: ValidationRules;
        public patientData!: Patient;
        public impfstelle_id!: number;
        public arzt!: number | null;
        public abrechnungLeistung!: number | null;
        public loadLeistungen!: () => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;
        public formatDate!: (input: string | null) => string | null;
        public parseDate!: (input: string | null) => string | null;
        public createLeistungseintrag!: (data: Impfung) => void;
        public deleteLeistungseintrag!: (data: Impfung) => void;
        public setDialogLeistung!: (visible: boolean) => void;
        public getSystemEinstellung!: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T>;
        public setSelectedAbrechnungLeistung!: (payload: Verrechnungsart) => void;
        public updateLeistungAbrechnung!: (payload: any) => void;

        public data = new Impfung();
        public leistungen:any;

        public readonly keineVerrechnung = Verrechnungsart.NichtVerrechenbar;
        public readonly mitVerrechnung = Verrechnungsart.Offen;
        public readonly leistungenFilter = createAutocompleteFilter<Leistung>('leistung', 'bezeichnung');

        public get isNew(): boolean {
            return this.data.patimpfung_id === null;
        }

        public get maxZeitraum(): string {
            return moment().format('YYYY-MM-DD');
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (!visible) return;

            this.data = new Impfung(this.value);

            if (this.isNew) {
                this.data.anzahl = 1;

                this.loadLeistungen();
                this.loadPersonen();

                this.data.patnr = this.patientData.patnr;
                this.data.impfstelle_id = this.impfstelle_id;
                this.data.impfdatum = moment().format('YYYY-MM-DD');
                this.data.person_id_durchf = this.arzt;
                this.data.abrechnung = this.abrechnungLeistung ?? Verrechnungsart.Offen;
            }

            this.impfdatumErrorMessages = [];
            this.impfdatumFormatted = this.formatDate(this.data.impfdatum);

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        }

        public onClickSave(): void {
            if (!this.$refs.form.validate() || this.impfdatumErrorMessages.length > 0) {
                return;
            }
            if (this.isHCWEnabled && this.data.hcw === 'n' && this.isNew && this.getSystemEinstellung('muw').isEnabled() &&
                (this.patientData.patientengruppe_id === Patgruppe.HCW_MUW || this.patientData.patientengruppe_id === Patgruppe.HCW_STUDENT)) {
                confirmSwal(
                    '',
                    'Ist Leistung HCW?',
                    'Ja',
                    'Nein')
                    .then(({ isConfirmed }: SweetAlertResult) => {
                        if (isConfirmed) {
                            this.data.hcw = 'j';
                        }
                        this.createLeistungseintrag(this.data);
                    });
            } else {
                this.createLeistungseintrag(this.data);
            }
        }

        public onClickDelete(): void {
            confirmSwal('Löschen', 'Leistungseintrag endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteLeistungseintrag(this.data);
                    }
                });
        }

        public close(): void {
            this.$emit('close');
            this.setDialogLeistung(false);
        }

        public impfdatumMenu = false;
        public impfdatumFormatted: string | null = null;
        public impfdatumErrorMessages: string[] = [];

        @Watch('data.impfdatum')
        public onChangeImpfdatum(date: string | null): void {
            this.impfdatumFormatted = this.formatDate(this.data.impfdatum);

            if (date !== null && this.isNew) {
                this.impfdatumErrorMessages = [];

                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.data.impfdatum);
                    if (valid !== true) {
                        this.impfdatumErrorMessages.push(valid as string);
                    }
                }

                if (this.data.impfdatum && (this.data.impfdatum > moment().format('YYYY-MM-DD'))) {
                    this.impfdatumErrorMessages.push('Datum darf nicht in der Zukunft sein.');
                }
            }
        }

        get isHCWEnabled(): boolean {
            if (this.data.leistung_id) {
                var leistung = this.leistungen.find((leistung: Impfung) => leistung.leistung_id === this.data.leistung_id);

                if (leistung?.hcw === '1') {
                    if (this.isNew) {
                        this.data.hcw = 'j';
                    }
                    return true;
                } else if (leistung?.hcw === '2') {
                    if (this.isNew) {
                        this.data.hcw = 'n';
                    }
                    return true;
                } else if (leistung?.hcw === '0') {
                    this.data.hcw = 'n';
                    return false;
                } else {
                    this.data.hcw = null;
                }
            }

            return false;
        }

        public onChangeAbrechnung(): void {
            this.setSelectedAbrechnungLeistung(this.data.abrechnung ?? Verrechnungsart.NichtVerrechenbar);

            if (!this.isNew) {
                this.updateLeistungAbrechnung({
                    patimpfung_id: this.data.patimpfung_id,
                    abrechnung: this.data.abrechnung ?? Verrechnungsart.NichtVerrechenbar,
                    hcw: this.data.hcw
                });
            }
        }
    }
